import React, { useState, useEffect, useCallback } from "react";
import {
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import { Clock, Coffee, LogIn, LogOut, Timer } from "lucide-react";
import "../styles/EmployeeAttendance.css";
import EmployeeTimeTracker from "../components/EmployeeTimeTracker";
// Import Axios instance
import apiClient from "../api/api";
import { DateTime } from "luxon";
import StatisticsPage from "../components/StatisticsPage";

const COLORS = ["#4F46E5", "#10B981", "#F59E0B", "#EF4444"];

const EmployeeAttendance = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10);

  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [publicHolidayList, setPublicHolidayList] = useState([]);
  const [weeklyData, setWeeklyData] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [statistics, setStatistics] = useState({
  today: 0,
  week: 0,
  month: 0,
  overtime: 0,
  expectedToday: 8.5,
  expectedWeek: 0,
  expectedMonth: 0,
});

  // Timer effect
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 600000); // Update every 10 minutes
    return () => clearInterval(timer);
  }, []);

  // Helper function to get week dates
  const getWeekDates = () => {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - dayOfWeek + 1); // Start from Monday

    const weekDates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      weekDates.push(date);
    }
    return weekDates;
  };

  // Helper function to check if date is today
  const isToday = (inputDate) => {
    try {
      const todayLondon = new Date(
        new Date().toLocaleString("en-US", { timeZone: "Europe/London" })
      );
      const checkDateLondon = new Date(
        new Date(inputDate).toLocaleString("en-US", {
          timeZone: "Europe/London",
        })
      );

      const todayStr = todayLondon.toISOString().split("T")[0];
      const checkStr = checkDateLondon.toISOString().split("T")[0];

      // console.log("Checking isToday:", inputDate, "=>", checkStr === todayStr);

      return checkStr === todayStr;
    } catch (error) {
      console.error("Error in isToday():", error);
      return false;
    }
  };

  // Helper function to check if date is in current week
  const isCurrentWeek = (date) => {
    const today = new Date();
    const checkDate = new Date(date);
    const startOfWeek = new Date(today);
    const dayOfWeek = today.getDay();
    startOfWeek.setDate(today.getDate() - dayOfWeek + 1);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);

    return checkDate >= startOfWeek && checkDate <= endOfWeek;
  };

  // Helper function to check if date is in current month
  const isCurrentMonth = (date) => {
    const today = new Date();
    const checkDate = new Date(date);
    return (
      checkDate.getMonth() === today.getMonth() &&
      checkDate.getFullYear() === today.getFullYear()
    );
  };

  // Helper function for London timezone (consistent with EmployeeTimeTracker)
  const getLondonTime = (date = new Date()) => {
    return new Date(
      date.toLocaleString("en-US", { timeZone: "Europe/London" })
    );
  };

  const formatTime = (dateTime) => {
    if (!dateTime) return "--:--";
    
    try {
      const londonTime = DateTime.fromISO(dateTime, { zone: "Europe/London" });

      return londonTime.toFormat("HH:mm");
    } catch (error) {
      console.error("Error formatting time:", error);
      return "--:--";
    }
  };

  const calculateBreakTime = (start, end) => {
    if (!start || !end) return "00:00";
    try {
      const startTime = new Date(start);
      const endTime = new Date(end);

      if (isNaN(startTime.getTime()) || isNaN(endTime.getTime()))
        return "00:00";

      const diff = endTime - startTime;
      const totalMinutes = Math.floor(diff / (1000 * 60));
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      return `${hours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}`;
    } catch (error) {
      console.error("Error calculating break time:", error);
      return "00:00";
    }
  };

  const calculateOvertime = (start, end) => {
    if (!start || !end) return "0h";
    try {
      const startTime = new Date(start);
      const endTime = new Date(end);

      if (isNaN(startTime.getTime()) || isNaN(endTime.getTime())) return "0h";

      const diff = endTime - startTime;
      const totalHours = Math.floor(diff / (1000 * 60 * 60));
      const standardHours = 8; // Assuming 8 hours is standard work day
      const overtime =
        totalHours > standardHours ? totalHours - standardHours : 0;

      return `${overtime}h`;
    } catch (error) {
      console.error("Error calculating overtime:", error);
      return "0h";
    }
  };

  const calculateTotalWorked = (start, end, breakStart, breakEnd) => {
    if (!start) return "--:--";
    try {
      const startTime = new Date(start);
      const endTime = end ? new Date(end) : currentTime;

      if (isNaN(startTime.getTime()) || isNaN(endTime.getTime()))
        return "--:--";

      let diff = endTime - startTime;

      // Subtract break duration if available
      if (breakStart && breakEnd) {
        const breakStartTime = new Date(breakStart);
        const breakEndTime = new Date(breakEnd);
        const breakDuration = breakEndTime - breakStartTime;
        if (!isNaN(breakDuration)) {
          diff -= breakDuration;
        }
      }

      const totalMinutes = Math.floor(diff / (1000 * 60));
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      return `${hours}h ${minutes}m`;
    } catch (error) {
      console.error("Error calculating total worked:", error);
      return "--:--";
    }
  };

  const calculateTotalHours = (start, end, breakStart, breakEnd) => {
    if (!start || !end) return 0;
    try {
      const startTime = new Date(start);
      const endTime = new Date(end);

      if (isNaN(startTime.getTime()) || isNaN(endTime.getTime())) return 0;

      let diff = endTime - startTime;

      // Subtract break duration if available
      if (breakStart && breakEnd) {
        const breakStartTime = new Date(breakStart);
        const breakEndTime = new Date(breakEnd);
        const breakDuration = breakEndTime - breakStartTime;
        if (!isNaN(breakDuration)) {
          diff -= breakDuration;
        }
      }

      const totalHours = diff / (1000 * 60 * 60); // Convert to hours

      return Math.round(totalHours * 100) / 100; // Round to 2 decimal places
    } catch (error) {
      console.error("Error calculating total hours:", error);
      return 0;
    }
  };

  // Fixed function to determine work type based on actual worked hours
  const getWorkType = (start, end, breakStart, breakEnd) => {
    if (!start) return "absent";

    try {
      const startTime = new Date(start);
      const endTime = end ? new Date(end) : new Date(); // Use current time if not checked out

      if (isNaN(startTime.getTime()) || isNaN(endTime.getTime()))
        return "absent";

      let totalWorkedMs = endTime - startTime;

      // Subtract break time if available
      if (breakStart && breakEnd) {
        const breakStartTime = new Date(breakStart);
        const breakEndTime = new Date(breakEnd);
        const breakDuration = breakEndTime - breakStartTime;
        if (!isNaN(breakDuration) && breakDuration > 0) {
          totalWorkedMs -= breakDuration;
        }
      }

      // Convert to hours
      const totalWorkedHours = totalWorkedMs / (1000 * 60 * 60);

      const FUll_DAY = 8; // Full day in hours
      const HALF_DAY = 4; // Half day in hours
      const SHORT_DAY = 0.0; // Short day in hours (e.g., less than 4 hours)

      // Determine work type based on worked hours
      if (totalWorkedHours >= FUll_DAY) {
        return "fullDay";
      } else if (totalWorkedHours >= HALF_DAY) {
        return "halfDay";
      } else if (totalWorkedHours >= SHORT_DAY) {
        return "shortDay";
      } else {
        return "absent";
      }
    } catch (error) {
      console.error("Error calculating work type:", error);
      return "absent";
    }
  };

  // Memoized fetch function to prevent infinite re-renders
  const fetchData = useCallback(async () => {
    setCurrentTime(new Date()); // Update currentTime whenever you fetch
    try {
      // console.log("Starting to fetch attendance data...");

      // First, try to fetch attendance data (main requirement)
      const attendanceRes = await apiClient.get("/attendance");
      // console.log("Attendance data received:", attendanceRes.data);

      const holidaysRes = await apiClient.get("/holidays");
      // console.log("Public holidays data received:", holidaysRes.data);
      setPublicHolidayList(holidaysRes.data.map(h => h.date));
      // console.log("Public holiday list set:", publicHolidayList);

      // Format Attendance Records - Sort by date (most recent first)
      const sortedAttendanceData = attendanceRes.data.sort(
        (a, b) => new Date(b.date) - new Date(a.date)
      );

      const formattedAttendance = sortedAttendanceData.map((record, index) => {
        const workType = getWorkType(
          record.clock_in,
          record.clock_out,
          record.break_start,
          record.break_end
        );

        return {
          id: record.id || index + 1,
          date: new Date(record.date).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          }),
          rawDate: record.date,
          CheckIn: formatTime(record.clock_in),
          CheckOut: formatTime(record.clock_out),
          breakTime: calculateBreakTime(record.break_start, record.break_end),
          breakStart: record.break_start,
          breakEnd: record.break_end,
          clockInRaw: record.clock_in,
          clockOutRaw: record.clock_out,
          workType: workType,
          fullDay: workType === "fullDay",
          halfDay: workType === "halfDay",
          shortDay: workType === "shortDay",
          absent: workType === "absent",
          overtime: calculateOvertime(record.clock_in, record.clock_out),
          totalProductive: calculateTotalWorked(
            record.clock_in,
            record.clock_out,
            record.break_start,
            record.break_end
          ),
          totalHours: calculateTotalHours(record.clock_in, record.clock_out, record.break_start, record.break_end),
        };
      });

      // console.log("Formatted attendance:", formattedAttendance);
      setAttendanceRecords(formattedAttendance);

      // Calculate Statistics
      const todayRecord = formattedAttendance.find((record) =>
        isToday(record.clockInRaw)
      );

      const weekRecords = formattedAttendance.filter((record) =>
        isCurrentWeek(record.rawDate)
      );
      const monthRecords = formattedAttendance.filter((record) =>
        isCurrentMonth(record.rawDate)
      );

      const todayHours = todayRecord ? todayRecord.totalHours : 0;
      const weekHours = weekRecords.reduce(
        (sum, record) => sum + record.totalHours,
        0
      );
      const monthHours = monthRecords.reduce(
        (sum, record) => sum + record.totalHours,
        0
      );
      const totalOvertime = formattedAttendance.reduce((sum, record) => {
        const overtime = parseFloat(record.overtime.replace("h", "")) || 0;
        return sum + overtime;
      }, 0);


      // Generate Chart Data for Last 7 Days
      const getLast7Days = () => {
        const days = [];
        for (let i = 6; i >= 0; i--) {
          const d = new Date();
          d.setDate(d.getDate() - i);
          days.push(new Date(d));
        }
        return days;
      };

      const last7Dates = getLast7Days();

      const weeklyChartData = last7Dates.map((date) => {
        const dayName = date.toLocaleDateString("en-US", {
          weekday: "short",
        });

        const dayRecord = formattedAttendance.find((record) => {
          const recordDate = new Date(record.rawDate);
          return recordDate.toDateString() === date.toDateString();
        });

        return {
          day: dayName,
          hours: dayRecord ? dayRecord.totalHours : 0,
          date: date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
          }),
        };
      });

      setWeeklyData(weeklyChartData);

      // Build Recent Activity from today's attendance data only - FIXED
      const activity = [];
      const todayRecords = attendanceRes.data.filter((record) =>
        isToday(record.clock_in)
      );

      // console.log("Today's records for activity:", todayRecords);

      todayRecords.forEach((record) => {
        if (record.clock_in) {
          activity.push({
            id: `checkin-${record.id}`,
            action: "Check In",
            time: formatTime(record.clock_in),
            type: "in",
            avatar: "IN",
            timestamp: new Date(record.clock_in),
          });
        }

        if (record.break_start) {
          activity.push({
            id: `breakstart-${record.id}`,
            action: "Break Start",
            time: formatTime(record.break_start),
            type: "break",
            avatar: "BR",
            timestamp: new Date(record.break_start),
          });
        }

        if (record.break_end) {
          activity.push({
            id: `breakend-${record.id}`,
            action: "Break End",
            time: formatTime(record.break_end),
            type: "break",
            avatar: "BR",
            timestamp: new Date(record.break_end),
          });
        }

        if (record.clock_out) {
          activity.push({
            id: `checkout-${record.id}`,
            action: "Check Out",
            time: formatTime(record.clock_out),
            type: "out",
            avatar: "OUT",
            timestamp: new Date(record.clock_out),
          });
        }
      });

      // Sort activities by timestamp (chronological order - earliest first)
      activity.sort((a, b) => a.timestamp - b.timestamp);
      // console.log("Today's activity:", activity);
      setRecentActivity(activity);
    } catch (err) {
      console.error("Failed to fetch attendance data:", err);
      console.error("Error details:", err.response?.data || err.message);
      // Set empty arrays to prevent rendering issues
      setAttendanceRecords([]);
      setWeeklyData([]);
      setRecentActivity([]);
    }
  }, []); // Removed currentTime as dependency for real-time updates as it was causing infinite re-renders

  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh data every 30 seconds to get new data without manual refresh
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      // console.log("Auto-refreshing attendance data...");
      fetchData();
    }, 600000); 

    return () => clearInterval(refreshInterval);
  }, [fetchData]);

  // Pagination Logic
  const indexOfLastRecord = currentPage * rowsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - rowsPerPage;
  const currentRecords = attendanceRecords.slice(
    indexOfFirstRecord,
    indexOfLastRecord
  );
  const totalPages = Math.ceil(attendanceRecords.length / rowsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const ActivityItem = ({ action, time, type, avatar }) => {
    const getIconAndColor = () => {
      switch (type) {
        case "in":
          return { icon: LogIn, colorClass: "green" };
        case "out":
          return { icon: LogOut, colorClass: "red" };
        case "break":
          return { icon: Coffee, colorClass: "yellow" };
        default:
          return { icon: Clock, colorClass: "blue" };
      }
    };

    const { icon: Icon, colorClass } = getIconAndColor();

    return (
      <div className="activity-item">
        <div className={`activity-icon ${colorClass}`}>
          <Icon size={16} />
        </div>
        <div className="activity-content">
          <p className="activity-action">{action}</p>
          <p className="activity-time">{time}</p>
        </div>
      </div>
    );
  };

  return (
    <div className="attendance-app">
      <div className="main-content">
        {/* Top section */}
        <div className="top-section">
          <EmployeeTimeTracker />
          <div className="card monthly-status">
            <h2 className="card-title">Weekly Hours</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={weeklyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="day" stroke="#9CA3AF" tick={{ fontSize: 12 }} />
                <YAxis stroke="#9CA3AF" tick={{ fontSize: 12 }} />
                <Tooltip
                  formatter={(value, name) => [`${Math.floor(value)}h:${Math.round((value % 1) * 60).toString().padStart(2, '0')}m`, "Hours Worked"]}
                  labelFormatter={(label) => {
                    const dayData = weeklyData.find((d) => d.day === label);
                    return `${label} (${dayData?.date})`;
                  }}
                />
                <Bar dataKey="hours" fill="#1e3a8a" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="dashboard-grid">
          {/* Statistics Card */}
          <StatisticsPage />

          {/* Recent Activity - Today Only */}
          <div className="card recent-activity">
            <h2 className="card-title">Today's Activity</h2>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",
              }}
            >
              {recentActivity.length > 0 ? (
                recentActivity
                  .slice(0, 6)
                  .map((activity) => (
                    <ActivityItem key={activity.id} {...activity} />
                  ))
              ) : (
                <p
                  style={{
                    textAlign: "center",
                    color: "#9CA3AF",
                    padding: "1rem",
                  }}
                >
                  No activity recorded for today
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeAttendance;
